import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { reorderSpaList } from '../../api/spaApi';
import { Box, Typography, Grid, CardContent, Card, Chip, Tooltip } from '@mui/material';
import { Plus } from 'lucide-react';
import SpaMenuCard from '../ui/Card/SpaMenuCard';
import { fetchSingleSpa } from '../../api/spaApi';
import EditableText from '../ui/EditableField/EditableText';
import ImageUploadDialog from '../ui/ImageUpload/ImageUploadDialog';
import useImageChangeHandler from '../../hooks/useImageChangeHandler';
import { uploadImage } from '../../api/imageApi';
import { updateSpa, updateSingleMassage, createSingleMassage, deleteSingleMassage } from '../../api/spaApi';
import { deleteRemovedImages } from '../../utils/imageUtils';
import SpaMenuFormModal from './SpaMenuFormModal';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import LoadingPlaceholder from '../ui/Loading/LoadingPlaceholder';

const truncateWords = (text, limit = 5) => {
  const words = text.split(' ');
  return words.length > limit ? words.slice(0, limit).join(' ') + '...' : text;
};

function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none'
  };

  // Drag handle: middle-left, vertical, listeners applied only to handle
  return (
    <div ref={setNodeRef} style={{ ...style, position: 'relative' }}>
      <div
        {...attributes}
        {...listeners}
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          cursor: 'grab',
          zIndex: 10,
          opacity: 0.4
        }}
      >
        <span style={{ fontSize: 20 }}>⠿</span>
      </div>
      {children}
    </div>
  );
}

export default function SpaMenu({ spaId, propertyId }) {
  const sensors = useSensors(useSensor(PointerSensor));
  const [treatments, setTreatments] = useState([]);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [newTreatment, setNewTreatment] = useState(null);
  const [editTreatment, setEditTreatment] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const handleImageChange = useImageChangeHandler(uploadTarget, setSelectedFile, setPreviewImage);

  useEffect(() => {
    if (!spaId || !propertyId) return;
    const fetchSpa = async () => {
      try {
        setLoading(true);
        const spa = await fetchSingleSpa(spaId, propertyId);
        setTreatments(spa?.massages || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSpa();
  }, [spaId, propertyId]);

  useEffect(() => {
  }, [formModalOpen, editTreatment, newTreatment]);

  useEffect(() => {
    if (editTreatment) {
      setFormModalOpen(true);
    }
  }, [editTreatment]);


  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Spa Menu
      </Typography>

      {loading ? (
        <LoadingPlaceholder />
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const oldIndex = treatments.findIndex(t => String(t._id) === active.id);
              const newIndex = treatments.findIndex(t => String(t._id) === over.id);
              const reordered = arrayMove(treatments, oldIndex, newIndex);
              setTreatments(reordered);
              reorderSpaList(spaId, reordered.map(m => ({
                ...m,
                promotion: typeof m.promotion === 'object' ? m.promotion?._id : m.promotion
              }))).catch(err => console.error('⚠️ Failed to reorder treatments:', err));
            }}
          >
            <SortableContext items={treatments.map(t => String(t._id))} strategy={rectSortingStrategy}>
              <Grid container spacing={3}>
                {treatments.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={item._id}>
                    <SortableItem id={String(item._id)}>
                      <Box sx={{ position: 'relative' }}>
                        {item.promotion && typeof item.promotion === 'object' && item.promotion?.title && (
                          (() => {
                            console.log('🧪 Promotion badge check:', item.typeOfMassage, item.promotion);
                            const now = new Date();
                            const start = new Date(item.promotion.startDate);
                            const end = new Date(item.promotion.endDate);
                            let promoColor = 'error.main';
                            if (item.promotion.status === 'inactive') {
                              promoColor = 'grey.600';
                            } else if (now < start) {
                              promoColor = 'info.main';
                            } else if (now > end) {
                              promoColor = 'grey.400';
                            }
                            if (item.promotion.status !== 'inactive' && now > end) {
                              return null;
                            }
                            return (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  backgroundColor: promoColor,
                                  color: 'white',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  zIndex: 1
                                }}
                              >
                                {item.promotion.title}
                                {item.promotion.discount ? ` - ${item.promotion.discount}% OFF` : ''}
                              </Box>
                            );
                          })()
                        )}
                        <SpaMenuCard
                          item={item}
                          onImageClick={() => {
                            setUploadTarget({ ...item, index, spaId, propertyId });
                            setPreviewImage(item.image || null);
                            setSelectedFile(item.image || null);
                          }}
                          onEdit={() => {
                            setEditTreatment(item);
                            setNewTreatment(null);
                            setFormModalOpen(true);
                          }}
                          onDelete={() => {
                            setDeletingItem(item);
                            setConfirmOpen(true);
                          }}
                        >
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Box>
                              <EditableText
                                key={`${item._id}-name-${item.typeOfMassage}`}
                                value={item.typeOfMassage || '—'}
                                onSave={(val) => {
                                  const updated = treatments.map(t =>
                                    t._id === item._id ? { ...t, typeOfMassage: val } : t
                                  );
                                  setTreatments(updated);
                                  updateSingleMassage(spaId, item._id, propertyId, { typeOfMassage: val });
                                }}
                                variant="subtitle1"
                                fontWeight="bold"
                              />

                              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                <Chip
                                  label={item.category || '—'}
                                  size="small"
                                  sx={{
                                    bgcolor: '#f0f0f0',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    color: '#555'
                                  }}
                                />
                              </Box>
                              
                              {item.description?.split(' ').length > 8 ? (
                                <Box sx={{ mt: 1, position: 'relative', '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  right: 0,
                                  width: '100%',
                                  height: '1.5em',
                                  background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff 90%)',
                                  pointerEvents: 'none'
                                } }}>
                                  <Tooltip title={item.description || '—'}>
                                    <span>
                                      <EditableText
                                        key={`${item._id}-desc`}
                                        value={item.description || '—'}
                                        displayValue={truncateWords(item.description || '', 8)}
                                        onSave={(val) => {
                                          const updated = treatments.map(t =>
                                            t._id === item._id ? { ...t, description: val } : t
                                          );
                                          setTreatments(updated);
                                          updateSingleMassage(spaId, item._id, propertyId, { description: val });
                                        }}
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          maxWidth: '240px',
                                          mx: 'auto',
                                          textAlign: 'center'
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Box sx={{ mt: 1 }}>
                                  <EditableText
                                    key={`${item._id}-desc`}
                                    value={item.description || '—'}
                                    onSave={(val) => {
                                      const updated = treatments.map(t =>
                                        t._id === item._id ? { ...t, description: val } : t
                                      );
                                      setTreatments(updated);
                                      updateSingleMassage(spaId, item._id, propertyId, { description: val });
                                    }}
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      maxWidth: '240px',
                                      mx: 'auto',
                                      textAlign: 'center'
                                    }}
                                  />
                                </Box>
                              )}
                              
                              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {Object.entries(item.prices || {}).map(([duration, price]) => (
                                  <Box key={duration} sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Grid container spacing={1} alignItems="center" justifyContent="center">
                                      <Grid item>
                                        <Typography variant="body2" color="text.secondary">
                                          {duration} min:
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <EditableText
                                          key={`${item._id}-price-${duration}`}
                                          value={price.toString()}
                                          type="number"
                                          onSave={(val) => {
                                            const updatedPrice = parseFloat(val);
                                            const updated = treatments.map(t =>
                                              t._id === item._id
                                                ? { ...t, prices: { ...t.prices, [duration]: updatedPrice } }
                                                : t
                                            );
                                            setTreatments(updated);
                                            updateSingleMassage(spaId, item._id, propertyId, {
                                              prices: { ...item.prices, [duration]: updatedPrice }
                                            });
                                          }}
                                          variant="body2"
                                          color="text.secondary"
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </CardContent>
                        </SpaMenuCard>
                      </Box>
                    </SortableItem>
                  </Grid>
                ))}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      width: 240,
                      height: 360,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ccc',
                      cursor: 'pointer',
                      borderRadius: 2
                    }}
                    onClick={() => {
                      setNewTreatment({
                        typeOfMassage: '',
                        description: '',
                        prices: {}
                      });
                      setFormModalOpen(true);
                    }}
                  >
                    <Plus size={40} color="#888" />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Add Treatment
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </SortableContext>
          </DndContext>
          {treatments.length === 0 && (
            <Typography>No treatments found for this spa.</Typography>
          )}
        </>
      )}
      <ImageUploadDialog
        open={!!uploadTarget}
        uploadTarget={uploadTarget}
        previewImage={previewImage}
        uploading={uploading}
        setUploading={setUploading}
        setUploadTarget={setUploadTarget}
        setSelectedFile={setSelectedFile}
        setPreviewImage={setPreviewImage}
        handleImageChange={handleImageChange}
        onSave={async (updatedImage) => {
          try {
            await updateSingleMassage(spaId, uploadTarget._id, propertyId, { image: updatedImage });
            const updated = treatments.map(t =>
              t._id === uploadTarget._id ? { ...t, image: updatedImage } : t
            );
            setTreatments(updated);
            deleteRemovedImages(
              uploadTarget?.image ? [uploadTarget.image] : [],
              updatedImage ? [updatedImage] : []
            );
          } catch (err) {
          }
        }}
      />
      <SpaMenuFormModal
        open={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditTreatment(null);
          setNewTreatment(null);
        }}
        initialData={editTreatment || newTreatment}
        editTreatment={editTreatment}
        propertyId={propertyId}
        onSubmit={async (data) => {
          try {
            if (editTreatment?._id) {
              await updateSingleMassage(spaId, editTreatment._id, propertyId, data);
            } else {
              await createSingleMassage(spaId, propertyId, data);
            }
            const spa = await fetchSingleSpa(spaId, propertyId);
            setTreatments(spa?.massages || []);
            setFormModalOpen(false);
            setEditTreatment(null);
            setNewTreatment(null);
          } catch (err) {
          }
        }}
      />
      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={async () => {
          try {
            await deleteSingleMassage(spaId, deletingItem._id, propertyId);
            setTreatments(treatments.filter(t => t._id !== deletingItem._id));
          } catch (err) {}
          setConfirmOpen(false);
          setDeletingItem(null);
        }}
        title="Delete Treatment"
        description="Are you sure you want to delete this treatment?"
      />
    </Box>
  );
}

